import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Email } from '@/types/email';
import type { AiAnnotation } from '@/types/ai';
import { fetchProductLineExtraction, runProductLineExtraction } from '@/api/aiTasksApi';
import { ApiError } from '@/api/core/client';

export interface ProductLineExtractionResult {
  manufacturer: string | null;
  productLine: string | null;
  description: string | null;
  launchDate: string | null;
  orderDeadline: string | null;
  notes: string | null;
  confidence: number | null;
}

interface UseProductLineExtractionResult {
  annotation: AiAnnotation | null;
  extraction: ProductLineExtractionResult | null;
  isLoading: boolean;
  error: string | null;
  hasFetched: boolean;
  requestExtraction: (options?: { force?: boolean }) => Promise<AiAnnotation | null>;
  resetError: () => void;
}

function coerceExtraction(annotation: AiAnnotation | null): ProductLineExtractionResult | null {
  if (!annotation?.result) {
    return null;
  }
  const result = annotation.result as Record<string, unknown>;
  if (!result || typeof result !== 'object') {
    return null;
  }

  const productLine = typeof result.productLine === 'string' ? result.productLine.trim() : null;

  // Must have at least a product line name to be valid
  if (!productLine) {
    return null;
  }

  return {
    manufacturer: typeof result.manufacturer === 'string' ? result.manufacturer.trim() : null,
    productLine,
    description: typeof result.description === 'string' ? result.description.trim() : null,
    launchDate: typeof result.launchDate === 'string' ? result.launchDate : null,
    orderDeadline: typeof result.orderDeadline === 'string' ? result.orderDeadline : null,
    notes: typeof result.notes === 'string' ? result.notes.trim() : null,
    confidence: typeof result.confidence === 'number' ? result.confidence : null,
  };
}

export function useProductLineExtraction(email: Email | null): UseProductLineExtractionResult {
  const [annotation, setAnnotation] = useState<AiAnnotation | null>(null);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);
  const emailId = email?.id ?? null;
  const accountId = email?.accountId ?? null;

  // Identity anchor for the EXPLICIT request path (X3): an extraction requested
  // for message A must never render under message B after navigation.
  const currentEmailIdRef = useRef<string | null>(emailId);

  // Reset state when email changes
  useEffect(() => {
    currentEmailIdRef.current = emailId;
    setAnnotation(null);
    setError(null);
    setHasFetched(false);
  }, [accountId, emailId]);

  const requestExtraction = useCallback(
    async (options?: { force?: boolean }) => {
      if (!accountId || !emailId) {
        return null;
      }
      const { force = false } = options || {};
      const requestEmailId = emailId;
      setError(null);
      setLoading(true);
      try {
        const result = await runProductLineExtraction({
          accountId,
          messageId: emailId,
          force,
          source: 'product_line_creation',
        });
        // Drop the response if the user navigated away mid-request (X3).
        if (currentEmailIdRef.current !== requestEmailId) {
          return null;
        }
        setAnnotation(result);
        setHasFetched(true);
        return result;
      } catch (err) {
        if (currentEmailIdRef.current !== requestEmailId) {
          return null;
        }
        if (err instanceof ApiError && err.status === 503) {
          setError('AI extraction is temporarily unavailable');
        } else {
          setError(err instanceof Error ? err.message : 'Failed to extract product line');
        }
        throw err;
      } finally {
        if (currentEmailIdRef.current === requestEmailId) {
          setLoading(false);
        }
      }
    },
    [accountId, emailId]
  );

  // Try to fetch existing annotation on mount (don't auto-run)
  useEffect(() => {
    if (!emailId || !accountId || hasFetched) {
      return;
    }
    let cancelled = false;
    fetchProductLineExtraction({ accountId, messageId: emailId })
      .then((result) => {
        if (cancelled) {
          return;
        }
        if (result) {
          setAnnotation(result);
        }
        setHasFetched(true);
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        // Ignore 404/409 errors - just means no cached extraction
        if (err instanceof ApiError && (err.status === 404 || err.status === 409)) {
          setHasFetched(true);
          return;
        }
        const message = err instanceof Error ? err.message : 'Failed to load extraction';
        setError(message);
        setHasFetched(true);
      });

    return () => {
      cancelled = true;
    };
  }, [accountId, emailId, hasFetched]);

  const extraction = useMemo(() => coerceExtraction(annotation), [annotation]);

  const resetError = useCallback(() => setError(null), []);

  return {
    annotation,
    extraction,
    isLoading,
    error,
    hasFetched,
    requestExtraction,
    resetError,
  };
}
